import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/auth';
import { Search, Settings, TrendingUp, Phone, MessageCircle, Calendar, Users, BarChart3, ArrowUpDown, ArrowUp, ArrowDown, ChevronDown } from 'lucide-react';
import { useAuth } from '../components/AuthProvider';
import LoadingSpinner from '../components/ui/LoadingSpinner';
import StatusBadge from '../components/ui/StatusBadge';
import DateRangePicker from '../components/ui/DateRangePicker';

interface CampaignStats {
  client_id: string;
  client_name: string;
  start_date: string | null;
  end_date: string | null;
  days_per_month: number | null;
  statut: string;
  contract_type: string | null;
  monthly_price: number | null;
  days_planned: number;
  days_done: number;
  schedule_progress_percent: number;
  total_calls: number;
  total_conversations: number;
  total_rdvs: number;
  conversion_conv_to_rdv: number;
  sdr_count: number;
}

interface ProspectingDay {
  id: string;
  sdr_id: string;
  client_id?: string;
  date: string;
  calls_count: number;
  conversations_count: number;
  rdvs_booked: number;
  sdr?: {
    sales_name: string;
  };
  client_name?: string | null;
}

interface SdrStats {
  sdr_id: string;
  sdr_name: string;
  nb_call: number;
  nb_call_from_logs: number;
  nb_echange: number;
  nb_rdv: number;
  nb_rdv_appointments: number;
  nb_rdv_prospecting: number;
  nb_connectes: number;
  nb_vocemails: number;
  jours_travailles: number;
  moy_calls_jour: number;
  moy_rdv_jour: number;
  taux_conversion: number;
  taux_joignabilite: number;
  taux_connexion: number;
}

interface AppointmentDetail {
  id: string;
  date: string;
  time: string;
  prospect_name: string | null;
  client_name: string | null;
  status: string;
}

interface ProspectingDayDetail {
  id: string;
  date: string;
  client_name: string | null;
  calls_count: number;
  conversations_count: number;
  rdvs_booked: number;
}

interface SdrProfile {
  user_id: string;
  sales_name: string;
}

const CampagnesOverviewPage: React.FC = () => {
  const navigate = useNavigate();
  const { profile } = useAuth();
  const [campaigns, setCampaigns] = useState<CampaignStats[]>([]);
  const [filteredCampaigns, setFilteredCampaigns] = useState<CampaignStats[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');

  const [prospectingData, setProspectingData] = useState<ProspectingDay[]>([]);
  const [appointmentsData, setAppointmentsData] = useState<any[]>([]);
  const [callLogsData, setCallLogsData] = useState<any[]>([]);
  const [allSdrs, setAllSdrs] = useState<SdrProfile[]>([]);
  const [sdrStatsLoading, setSdrStatsLoading] = useState(false);
  const [dateRangeStart, setDateRangeStart] = useState<string | null>(null);
  const [dateRangeEnd, setDateRangeEnd] = useState<string | null>(null);
  const [periodFilter, setPeriodFilter] = useState<'today' | 'week' | 'month' | 'custom'>('month');
  const [sortField, setSortField] = useState<keyof SdrStats>('nb_call');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  const [activeTab, setActiveTab] = useState<'campaigns' | 'stats'>('campaigns');
  const [selectedSdrId, setSelectedSdrId] = useState<string | null>(null);
  const [detailsLoading, setDetailsLoading] = useState(false);

  useEffect(() => {
    if (profile?.role !== 'admin' && profile?.role !== 'super_admin') {
      navigate('/');
      return;
    }
    fetchCampaigns();
  }, [profile]);

  useEffect(() => {
    filterCampaigns();
  }, [searchTerm, statusFilter, campaigns]);

  useEffect(() => {
    if (profile?.role === 'super_admin' && activeTab === 'stats') {
      fetchSdrStats();
    }
  }, [profile, periodFilter, dateRangeStart, dateRangeEnd, activeTab]);

  useEffect(() => {
    if (periodFilter !== 'custom') {
      const dates = getDateRangeForPeriod(periodFilter);
      setDateRangeStart(dates.start);
      setDateRangeEnd(dates.end);
    }
  }, [periodFilter]);

  const fetchCampaigns = async () => {
    try {
      setLoading(true);

      const { data: clients, error: clientsError } = await supabase
        .from('clients')
        .select('*')
        .order('name');

      if (clientsError) throw clientsError;

      const campaignPromises = (clients || []).map(async (client) => {
        const { data: prospectingData } = await supabase
          .from('prospecting_days')
          .select('date, calls_count, conversations_count, rdvs_booked, sdr_id')
          .eq('client_id', client.id);

        const { data: appointmentsData } = await supabase
          .from('appointments')
          .select('id')
          .eq('client_id', client.id);

        const uniqueDates = new Set(prospectingData?.map(d => d.date) || []);
        const uniqueSdrs = new Set(prospectingData?.filter(d => d.sdr_id).map(d => d.sdr_id) || []);

        const totalCalls = prospectingData?.reduce((sum, d) => sum + (d.calls_count || 0), 0) || 0;
        const totalConversations = prospectingData?.reduce((sum, d) => sum + (d.conversations_count || 0), 0) || 0;
        const totalRdvs = appointmentsData?.length || 0;

        let daysPlanned = client.days_per_month || 0;
        if (client.start_date && client.end_date && client.days_per_month) {
          const startDate = new Date(client.start_date);
          const endDate = new Date(client.end_date);
          const monthsDiff = (endDate.getTime() - startDate.getTime()) / (30 * 24 * 60 * 60 * 1000);
          daysPlanned = Math.max(Math.round(monthsDiff * client.days_per_month), client.days_per_month);
        }

        const daysDone = uniqueDates.size;
        const progressPercent = daysPlanned > 0 ? Math.round((daysDone / daysPlanned) * 100) : 0;
        const conversionRate = totalConversations > 0 ? Math.round((totalRdvs / totalConversations) * 100) : 0;

        return {
          client_id: client.id,
          client_name: client.name,
          start_date: client.start_date,
          end_date: client.end_date,
          days_per_month: client.days_per_month,
          statut: client.statut,
          contract_type: client.contract_type,
          monthly_price: client.monthly_price,
          days_planned: daysPlanned,
          days_done: daysDone,
          schedule_progress_percent: progressPercent,
          total_calls: totalCalls,
          total_conversations: totalConversations,
          total_rdvs: totalRdvs,
          conversion_conv_to_rdv: conversionRate,
          sdr_count: uniqueSdrs.size,
        };
      });

      const campaignsData = await Promise.all(campaignPromises);
      setCampaigns(campaignsData);
    } catch (error) {
      console.error('Error fetching campaigns:', error);
    } finally {
      setLoading(false);
    }
  };

  const filterCampaigns = () => {
    let filtered = campaigns;

    if (searchTerm) {
      filtered = filtered.filter(c =>
        c.client_name.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    if (statusFilter !== 'all') {
      filtered = filtered.filter(c => c.statut === statusFilter);
    }

    setFilteredCampaigns(filtered);
  };

  const getDateRangeForPeriod = (period: 'today' | 'week' | 'month' | 'custom') => {
    const today = new Date();
    const start = new Date();
    let end = new Date();

    const formatLocalDate = (date: Date) => {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    };

    switch (period) {
      case 'today':
        start.setHours(0, 0, 0, 0);
        end.setHours(23, 59, 59, 999);
        break;
      case 'week':
        const dayOfWeek = today.getDay();
        const diff = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
        start.setDate(today.getDate() - diff);
        start.setHours(0, 0, 0, 0);
        end.setDate(start.getDate() + 6);
        end.setHours(23, 59, 59, 999);
        break;
      case 'month':
        start.setDate(1);
        start.setHours(0, 0, 0, 0);
        end = new Date(today.getFullYear(), today.getMonth() + 1, 0);
        end.setHours(23, 59, 59, 999);
        break;
    }

    return {
      start: formatLocalDate(start),
      end: formatLocalDate(end)
    };
  };

  const fetchSdrStats = async () => {
    if (!dateRangeStart || !dateRangeEnd) return;

    try {
      setSdrStatsLoading(true);

      // First, get prospecting days data for the date range
      const { data, error } = await supabase
        .from('prospecting_days')
        .select(`
          id,
          sdr_id,
          client_id,
          date,
          calls_count,
          conversations_count,
          rdvs_booked,
          profiles!prospecting_days_sdr_id_fkey (
            sales_name
          ),
          clients!prospecting_days_client_id_fkey (
            name
          )
        `)
        .gte('date', dateRangeStart)
        .lte('date', dateRangeEnd)
        .not('sdr_id', 'is', null);

      if (error) throw error;

      // Extract unique user IDs from prospecting days data
      const uniqueSdrIds = [...new Set((data || []).map(item => item.sdr_id))];

      // Get profiles for all users with prospecting data (SDR, admin, super_admin)
      const { data: sdrsData, error: sdrsError } = await supabase
        .from('profiles')
        .select('user_id, sales_name')
        .in('user_id', uniqueSdrIds)
        .not('sales_name', 'is', null)
        .order('sales_name');

      if (sdrsError) throw sdrsError;

      setAllSdrs(sdrsData || []);

      const formattedData = (data || []).map(item => ({
        id: item.id,
        sdr_id: item.sdr_id,
        client_id: item.client_id,
        date: item.date,
        calls_count: item.calls_count || 0,
        conversations_count: item.conversations_count || 0,
        rdvs_booked: item.rdvs_booked || 0,
        sdr: item.profiles ? { sales_name: item.profiles.sales_name } : undefined,
        client_name: item.clients?.name || null
      }));

      setProspectingData(formattedData);

      const nextDay = new Date(dateRangeEnd);
      nextDay.setDate(nextDay.getDate() + 1);
      const endTimestamp = nextDay.toISOString().split('T')[0];

      const { data: appointmentsData, error: appointmentsError } = await supabase
        .from('appointments')
        .select(`
          id,
          sdr_id,
          date,
          time,
          prospect_name,
          status,
          clients!appointments_client_id_fkey (
            name
          ),
          profiles!appointments_sdr_id_fkey (
            sales_name
          )
        `)
        .gte('created_at', dateRangeStart)
        .lt('created_at', endTimestamp)
        .not('sdr_id', 'is', null);

      if (appointmentsError) throw appointmentsError;

      setAppointmentsData(appointmentsData || []);

      const { data: callLogsData, error: callLogsError } = await supabase
        .from('call_logs')
        .select(`
          id,
          sdr_profile_id,
          status,
          created_at
        `)
        .gte('created_at', dateRangeStart)
        .lt('created_at', endTimestamp)
        .not('sdr_profile_id', 'is', null);

      if (callLogsError) throw callLogsError;

      console.log('📞 Call logs récupérés:', callLogsData?.length || 0);
      console.log('📞 Exemples de call logs:', callLogsData?.slice(0, 3));

      setCallLogsData(callLogsData || []);

      // Extract unique SDR IDs from call_logs as well
      const uniqueSdrIdsFromCallLogs = [...new Set((callLogsData || []).map(item => item.sdr_profile_id))];

      // Combine with SDR IDs from prospecting_days
      const allUniqueSdrIds = [...new Set([...uniqueSdrIds, ...uniqueSdrIdsFromCallLogs])];

      // Fetch profiles for ALL SDRs (from both prospecting_days and call_logs)
      if (allUniqueSdrIds.length > uniqueSdrIds.length) {
        const { data: additionalSdrs, error: additionalSdrsError } = await supabase
          .from('profiles')
          .select('user_id, sales_name')
          .in('user_id', allUniqueSdrIds)
          .not('sales_name', 'is', null)
          .order('sales_name');

        if (!additionalSdrsError && additionalSdrs) {
          setAllSdrs(additionalSdrs);
          console.log('✅ Profils SDR mis à jour avec call_logs:', additionalSdrs.length);
        }
      }
    } catch (error) {
      console.error('Error fetching SDR stats:', error);
    } finally {
      setSdrStatsLoading(false);
    }
  };

  const sdrStats = useMemo(() => {
    const statsMap = new Map<string, SdrStats>();

    allSdrs.forEach(sdr => {
      statsMap.set(sdr.user_id, {
        sdr_id: sdr.user_id,
        sdr_name: sdr.sales_name || 'SDR Inconnu',
        nb_call: 0,
        nb_call_from_logs: 0,
        nb_echange: 0,
        nb_rdv: 0,
        nb_rdv_appointments: 0,
        nb_rdv_prospecting: 0,
        nb_connectes: 0,
        nb_vocemails: 0,
        jours_travailles: 0,
        moy_calls_jour: 0,
        moy_rdv_jour: 0,
        taux_conversion: 0,
        taux_joignabilite: 0,
        taux_connexion: 0
      });
    });

    prospectingData.forEach(day => {
      if (!day.sdr_id) return;

      const existing = statsMap.get(day.sdr_id);
      const sdrName = day.sdr?.sales_name || 'SDR Inconnu';

      if (existing) {
        existing.nb_call += day.calls_count;
        existing.nb_echange += day.conversations_count;
        existing.nb_rdv_prospecting += day.rdvs_booked;
        const uniqueDates = new Set([...Array(existing.jours_travailles), day.date]);
        existing.jours_travailles = uniqueDates.size;
      } else {
        statsMap.set(day.sdr_id, {
          sdr_id: day.sdr_id,
          sdr_name: sdrName,
          nb_call: day.calls_count,
          nb_call_from_logs: 0,
          nb_echange: day.conversations_count,
          nb_rdv: 0,
          nb_rdv_appointments: 0,
          nb_rdv_prospecting: day.rdvs_booked,
          nb_connectes: 0,
          nb_vocemails: 0,
          jours_travailles: 1,
          moy_calls_jour: 0,
          moy_rdv_jour: 0,
          taux_conversion: 0,
          taux_joignabilite: 0,
          taux_connexion: 0
        });
      }
    });

    appointmentsData.forEach(appointment => {
      if (!appointment.sdr_id) return;

      const existing = statsMap.get(appointment.sdr_id);
      if (existing) {
        existing.nb_rdv_appointments += 1;
      } else {
        const sdrName = appointment.profiles?.sales_name || 'SDR Inconnu';
        statsMap.set(appointment.sdr_id, {
          sdr_id: appointment.sdr_id,
          sdr_name: sdrName,
          nb_call: 0,
          nb_call_from_logs: 0,
          nb_echange: 0,
          nb_rdv: 0,
          nb_rdv_appointments: 1,
          nb_rdv_prospecting: 0,
          nb_connectes: 0,
          nb_vocemails: 0,
          jours_travailles: 0,
          moy_calls_jour: 0,
          moy_rdv_jour: 0,
          taux_conversion: 0,
          taux_joignabilite: 0,
          taux_connexion: 0
        });
      }
    });

    console.log('🔍 Traitement des call logs, total:', callLogsData.length);

    const statusCounts: Record<string, number> = {};
    callLogsData.forEach(callLog => {
      if (!callLog.sdr_profile_id) return;

      statusCounts[callLog.status] = (statusCounts[callLog.status] || 0) + 1;

      const existing = statsMap.get(callLog.sdr_profile_id);
      if (existing) {
        existing.nb_call_from_logs += 1;

        if (callLog.status === 'connected') {
          existing.nb_connectes += 1;
        } else if (callLog.status === 'voicemail') {
          existing.nb_vocemails += 1;
        }
      } else {
        const sdrFromList = allSdrs.find(s => s.user_id === callLog.sdr_profile_id);
        const sdrName = sdrFromList?.sales_name || 'SDR Inconnu';

        statsMap.set(callLog.sdr_profile_id, {
          sdr_id: callLog.sdr_profile_id,
          sdr_name: sdrName,
          nb_call: 0,
          nb_call_from_logs: 1,
          nb_echange: 0,
          nb_rdv: 0,
          nb_rdv_appointments: 0,
          nb_rdv_prospecting: 0,
          nb_connectes: callLog.status === 'connected' ? 1 : 0,
          nb_vocemails: callLog.status === 'voicemail' ? 1 : 0,
          jours_travailles: 0,
          moy_calls_jour: 0,
          moy_rdv_jour: 0,
          taux_conversion: 0,
          taux_joignabilite: 0,
          taux_connexion: 0
        });
      }
    });

    console.log('📊 Répartition des status:', statusCounts);
    console.log('📊 Nombre de SDR dans statsMap après call logs:', statsMap.size);
    console.log('📊 SDR IDs uniques dans call logs:', new Set(callLogsData.map(cl => cl.sdr_profile_id)).size);

    const groupedBySDR = new Map<string, string[]>();
    prospectingData.forEach(day => {
      if (!day.sdr_id) return;
      if (!groupedBySDR.has(day.sdr_id)) {
        groupedBySDR.set(day.sdr_id, []);
      }
      groupedBySDR.get(day.sdr_id)!.push(day.date);
    });

    groupedBySDR.forEach((dates, sdrId) => {
      const stat = statsMap.get(sdrId);
      if (stat) {
        stat.jours_travailles = new Set(dates).size;
      }
    });

    statsMap.forEach(stat => {
      stat.nb_rdv = stat.nb_rdv_appointments;
      stat.moy_calls_jour = stat.jours_travailles > 0 ? Math.round(stat.nb_call / stat.jours_travailles) : 0;
      stat.moy_rdv_jour = stat.jours_travailles > 0 ? parseFloat((stat.nb_rdv / stat.jours_travailles).toFixed(2)) : 0;
      stat.taux_conversion = stat.nb_echange > 0 ? parseFloat(((stat.nb_rdv / stat.nb_echange) * 100).toFixed(1)) : 0;
      stat.taux_joignabilite = stat.nb_call > 0 ? parseFloat(((stat.nb_echange / stat.nb_call) * 100).toFixed(1)) : 0;
      stat.taux_connexion = stat.nb_call_from_logs > 0 ? parseFloat(((stat.nb_connectes / stat.nb_call_from_logs) * 100).toFixed(1)) : 0;
    });

    const totalConnectes = Array.from(statsMap.values()).reduce((sum, s) => sum + s.nb_connectes, 0);
    const totalVocemails = Array.from(statsMap.values()).reduce((sum, s) => sum + s.nb_vocemails, 0);
    console.log('✅ Stats finales - Connectés:', totalConnectes, 'Vocemails:', totalVocemails);

    return Array.from(statsMap.values());
  }, [allSdrs, prospectingData, appointmentsData, callLogsData]);

  const sortedSdrStats = useMemo(() => {
    return [...sdrStats].sort((a, b) => {
      const aVal = a[sortField];
      const bVal = b[sortField];

      if (typeof aVal === 'number' && typeof bVal === 'number') {
        return sortDirection === 'asc' ? aVal - bVal : bVal - aVal;
      }

      if (typeof aVal === 'string' && typeof bVal === 'string') {
        return sortDirection === 'asc'
          ? aVal.localeCompare(bVal)
          : bVal.localeCompare(aVal);
      }

      return 0;
    });
  }, [sdrStats, sortField, sortDirection]);

  const totalStats = useMemo(() => {
    const totalCallFromLogs = sdrStats.reduce((sum, s) => sum + s.nb_call_from_logs, 0);
    const totalConnectes = sdrStats.reduce((sum, s) => sum + s.nb_connectes, 0);

    return {
      nb_call: sdrStats.reduce((sum, s) => sum + s.nb_call, 0),
      nb_call_from_logs: totalCallFromLogs,
      nb_echange: sdrStats.reduce((sum, s) => sum + s.nb_echange, 0),
      nb_rdv: sdrStats.reduce((sum, s) => sum + s.nb_rdv, 0),
      nb_rdv_appointments: sdrStats.reduce((sum, s) => sum + s.nb_rdv_appointments, 0),
      nb_rdv_prospecting: sdrStats.reduce((sum, s) => sum + s.nb_rdv_prospecting, 0),
      nb_connectes: totalConnectes,
      nb_vocemails: sdrStats.reduce((sum, s) => sum + s.nb_vocemails, 0),
      jours_travailles: sdrStats.reduce((sum, s) => sum + s.jours_travailles, 0),
      moy_calls_jour: sdrStats.length > 0
        ? Math.round(sdrStats.reduce((sum, s) => sum + s.moy_calls_jour, 0) / sdrStats.length)
        : 0,
      moy_rdv_jour: sdrStats.length > 0
        ? parseFloat((sdrStats.reduce((sum, s) => sum + s.moy_rdv_jour, 0) / sdrStats.length).toFixed(2))
        : 0,
      taux_conversion: sdrStats.reduce((sum, s) => sum + s.nb_echange, 0) > 0
        ? parseFloat(((sdrStats.reduce((sum, s) => sum + s.nb_rdv, 0) / sdrStats.reduce((sum, s) => sum + s.nb_echange, 0)) * 100).toFixed(1))
        : 0,
      taux_joignabilite: sdrStats.reduce((sum, s) => sum + s.nb_call, 0) > 0
        ? parseFloat(((sdrStats.reduce((sum, s) => sum + s.nb_echange, 0) / sdrStats.reduce((sum, s) => sum + s.nb_call, 0)) * 100).toFixed(1))
        : 0,
      taux_connexion: totalCallFromLogs > 0
        ? parseFloat(((totalConnectes / totalCallFromLogs) * 100).toFixed(1))
        : 0
    };
  }, [sdrStats]);

  const selectedSdrDetails = useMemo(() => {
    if (!selectedSdrId) return { appointments: [], prospectingDays: [], callLogs: [] };

    const appointments = appointmentsData
      .filter(apt => apt.sdr_id === selectedSdrId)
      .map(apt => ({
        id: apt.id,
        date: apt.date,
        time: apt.time,
        prospect_name: apt.prospect_name,
        client_name: apt.clients?.name || null,
        status: apt.status
      }))
      .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());

    const prospectingDays = prospectingData
      .filter(day => day.sdr_id === selectedSdrId)
      .map(day => ({
        id: day.id,
        date: day.date,
        client_name: day.client_name,
        calls_count: day.calls_count,
        conversations_count: day.conversations_count,
        rdvs_booked: day.rdvs_booked
      }))
      .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());

    const callLogs = callLogsData
      .filter(log => log.sdr_profile_id === selectedSdrId)
      .map(log => ({
        id: log.id,
        call_type: log.call_type,
        created_at: log.created_at
      }))
      .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());

    return { appointments, prospectingDays, callLogs };
  }, [selectedSdrId, appointmentsData, prospectingData, callLogsData]);

  const handleSort = (field: keyof SdrStats) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };

  const handleDateRangeChange = (start: string | null, end: string | null) => {
    setDateRangeStart(start);
    setDateRangeEnd(end);
    if (start && end) {
      setPeriodFilter('custom');
    }
  };

  const getPerformanceColor = (value: number, field: string) => {
    if (field === 'taux_conversion') {
      if (value >= 15) return 'text-green-600 font-semibold';
      if (value >= 10) return 'text-yellow-600';
      return 'text-red-600';
    }
    if (field === 'taux_joignabilite') {
      if (value >= 30) return 'text-green-600 font-semibold';
      if (value >= 20) return 'text-yellow-600';
      return 'text-red-600';
    }
    if (field === 'taux_connexion') {
      if (value >= 30) return 'text-green-600 font-semibold';
      if (value >= 20) return 'text-yellow-600';
      return 'text-red-600';
    }
    return 'text-gray-900';
  };

  const getStatusColor = (statut: string) => {
    switch (statut.toLowerCase()) {
      case 'actif':
        return 'bg-green-100 text-green-800';
      case 'en pause':
        return 'bg-yellow-100 text-yellow-800';
      case 'inactif':
      case 'terminé':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getProgressColor = (percent: number) => {
    if (percent >= 80) return 'bg-green-500';
    if (percent >= 50) return 'bg-yellow-500';
    return 'bg-red-500';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Campagnes</h1>
          <p className="text-gray-600">Vue d'ensemble de toutes les campagnes et statistiques</p>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 mb-6">
          <div className="border-b border-gray-200">
            <nav className="flex -mb-px">
              <button
                onClick={() => setActiveTab('campaigns')}
                className={`py-4 px-6 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === 'campaigns'
                    ? 'border-blue-600 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <div className="flex items-center space-x-2">
                  <BarChart3 className="h-5 w-5" />
                  <span>Campagnes Client</span>
                </div>
              </button>
              {profile?.role === 'super_admin' && (
                <button
                  onClick={() => setActiveTab('stats')}
                  className={`py-4 px-6 text-sm font-medium border-b-2 transition-colors ${
                    activeTab === 'stats'
                      ? 'border-blue-600 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <div className="flex items-center space-x-2">
                    <Users className="h-5 w-5" />
                    <span>Stats SDR</span>
                  </div>
                </button>
              )}
            </nav>
          </div>
        </div>

        {activeTab === 'campaigns' && (
          <>
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div className="bg-gradient-to-br from-blue-50 to-blue-100 p-4 rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <Phone className="h-6 w-6 text-blue-600" />
                  </div>
                  <div className="text-2xl font-bold text-blue-900">
                    {campaigns.reduce((sum, c) => sum + c.total_calls, 0).toLocaleString()}
                  </div>
                  <div className="text-sm text-blue-700">Total Appels</div>
                </div>

                <div className="bg-gradient-to-br from-purple-50 to-purple-100 p-4 rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <MessageCircle className="h-6 w-6 text-purple-600" />
                  </div>
                  <div className="text-2xl font-bold text-purple-900">
                    {campaigns.reduce((sum, c) => sum + c.total_conversations, 0).toLocaleString()}
                  </div>
                  <div className="text-sm text-purple-700">Conversations</div>
                </div>

                <div className="bg-gradient-to-br from-green-50 to-green-100 p-4 rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <Calendar className="h-6 w-6 text-green-600" />
                  </div>
                  <div className="text-2xl font-bold text-green-900">
                    {campaigns.reduce((sum, c) => sum + c.total_rdvs, 0)}
                  </div>
                  <div className="text-sm text-green-700">RDV Bookés</div>
                </div>

                <div className="bg-gradient-to-br from-amber-50 to-amber-100 p-4 rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <TrendingUp className="h-6 w-6 text-amber-600" />
                  </div>
                  <div className="text-2xl font-bold text-amber-900">
                    {campaigns.length}
                  </div>
                  <div className="text-sm text-amber-700">Campagnes Actives</div>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow-sm border border-gray-200 mb-6">
              <div className="p-6 border-b border-gray-200">
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                  <div className="relative flex-1 max-w-md">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                    <input
                      type="text"
                      placeholder="Rechercher une campagne..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                    />
                  </div>

                  <div className="flex gap-2">
                    <button
                      onClick={() => setStatusFilter('all')}
                      className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                        statusFilter === 'all'
                          ? 'bg-amber-500 text-white'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      Tous
                    </button>
                    <button
                      onClick={() => setStatusFilter('actif')}
                      className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                        statusFilter === 'actif'
                          ? 'bg-green-500 text-white'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      Actifs
                    </button>
                    <button
                      onClick={() => setStatusFilter('en pause')}
                      className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                        statusFilter === 'en pause'
                          ? 'bg-yellow-500 text-white'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      En pause
                    </button>
                    <button
                      onClick={() => setStatusFilter('inactif')}
                      className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                        statusFilter === 'inactif'
                          ? 'bg-red-500 text-white'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      Terminés
                    </button>
                  </div>
                </div>
              </div>

              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Client</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Dates</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Jours</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Progression</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">RDV</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Conversion</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">SDRs</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Statut</th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {filteredCampaigns.map((campaign, idx) => (
                      <tr key={campaign.client_id} className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="font-medium text-gray-900">{campaign.client_name}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                          {campaign.start_date && campaign.end_date ? (
                            <div>
                              <div>{new Date(campaign.start_date).toLocaleDateString('fr-FR')}</div>
                              <div className="text-gray-400">→ {new Date(campaign.end_date).toLocaleDateString('fr-FR')}</div>
                            </div>
                          ) : (
                            <span className="text-gray-400">Non défini</span>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm">
                          <div className="font-semibold text-gray-900">{campaign.days_done} / {campaign.days_planned}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center gap-2">
                            <div className="flex-1 bg-gray-200 rounded-full h-2 w-24">
                              <div
                                className={`h-2 rounded-full ${getProgressColor(campaign.schedule_progress_percent)}`}
                                style={{ width: `${Math.min(campaign.schedule_progress_percent, 100)}%` }}
                              />
                            </div>
                            <span className="text-sm font-medium text-gray-700">{campaign.schedule_progress_percent}%</span>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center gap-1 text-green-600">
                            <Calendar className="h-4 w-4" />
                            <span className="font-semibold">{campaign.total_rdvs}</span>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="text-sm font-medium text-amber-600">{campaign.conversion_conv_to_rdv}%</span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center gap-1">
                            <Users className="h-4 w-4 text-gray-400" />
                            <span className="text-sm font-medium text-gray-700">{campaign.sdr_count}</span>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(campaign.statut)}`}>
                            {campaign.statut}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right">
                          <button
                            onClick={() => navigate(`/campagnes/${campaign.client_id}`)}
                            className="inline-flex items-center gap-1 px-3 py-1.5 bg-amber-500 text-white text-sm font-medium rounded-lg hover:bg-amber-600 transition-colors"
                          >
                            <Settings className="h-4 w-4" />
                            Ouvrir
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                {filteredCampaigns.length === 0 && (
                  <div className="text-center py-12">
                    <BarChart3 className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                    <p className="text-gray-500">Aucune campagne trouvée</p>
                  </div>
                )}
              </div>
            </div>
          </>
        )}

        {activeTab === 'stats' && profile?.role === 'super_admin' && (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200">
            <div className="p-6 border-b border-gray-200">
              <div className="flex flex-col gap-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <Users className="h-6 w-6 text-blue-600" />
                    <div>
                      <h2 className="text-xl font-bold text-gray-900">Stats SDR</h2>
                      <p className="text-sm text-gray-600">Performances des SDRs sur la période sélectionnée</p>
                    </div>
                  </div>
                </div>

                <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
                  <div className="flex gap-2">
                    <button
                      onClick={() => setPeriodFilter('today')}
                      className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                        periodFilter === 'today'
                          ? 'bg-blue-600 text-white'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      Aujourd'hui
                    </button>
                    <button
                      onClick={() => setPeriodFilter('week')}
                      className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                        periodFilter === 'week'
                          ? 'bg-blue-600 text-white'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      Cette semaine
                    </button>
                    <button
                      onClick={() => setPeriodFilter('month')}
                      className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                        periodFilter === 'month'
                          ? 'bg-blue-600 text-white'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      Ce mois
                    </button>
                  </div>

                  <div className="flex items-center gap-2">
                    <DateRangePicker onDateRangeChange={handleDateRangeChange} />
                    {periodFilter === 'custom' && dateRangeStart && dateRangeEnd && (
                      <span className="text-xs text-gray-500 bg-gray-100 px-3 py-2 rounded-lg">
                        {new Date(dateRangeStart).toLocaleDateString('fr-FR')} - {new Date(dateRangeEnd).toLocaleDateString('fr-FR')}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </div>

            <div className="overflow-x-auto">
              {sdrStatsLoading ? (
                <div className="flex items-center justify-center py-12">
                  <LoadingSpinner />
                </div>
              ) : (
                <table className="w-full">
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                      <th
                        className="sticky left-0 z-10 px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors bg-gray-50 border-r border-gray-300 shadow-[2px_0_4px_rgba(0,0,0,0.1)]"
                        onClick={() => handleSort('sdr_name')}
                      >
                        <div className="flex items-center space-x-1">
                          <span>SDR</span>
                          {sortField === 'sdr_name' && (
                            sortDirection === 'asc' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />
                          )}
                        </div>
                      </th>
                      <th
                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors"
                        onClick={() => handleSort('nb_call')}
                      >
                        <div className="flex items-center space-x-1">
                          <span>NB Call (Prosp.)</span>
                          {sortField === 'nb_call' && (
                            sortDirection === 'asc' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />
                          )}
                        </div>
                      </th>
                      <th
                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors"
                        onClick={() => handleSort('nb_call_from_logs')}
                      >
                        <div className="flex items-center space-x-1">
                          <span>Total Appels (Logs)</span>
                          {sortField === 'nb_call_from_logs' && (
                            sortDirection === 'asc' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />
                          )}
                        </div>
                      </th>
                      <th
                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors"
                        onClick={() => handleSort('nb_echange')}
                      >
                        <div className="flex items-center space-x-1">
                          <span>NB Échange</span>
                          {sortField === 'nb_echange' && (
                            sortDirection === 'asc' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />
                          )}
                        </div>
                      </th>
                      <th
                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors"
                        onClick={() => handleSort('nb_connectes')}
                      >
                        <div className="flex items-center space-x-1">
                          <span>Connectés</span>
                          {sortField === 'nb_connectes' && (
                            sortDirection === 'asc' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />
                          )}
                        </div>
                      </th>
                      <th
                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors"
                        onClick={() => handleSort('nb_vocemails')}
                      >
                        <div className="flex items-center space-x-1">
                          <span>Vocemails</span>
                          {sortField === 'nb_vocemails' && (
                            sortDirection === 'asc' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />
                          )}
                        </div>
                      </th>
                      <th
                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors"
                        onClick={() => handleSort('nb_rdv_appointments')}
                      >
                        <div className="flex items-center space-x-1">
                          <span>RDV (Appointments)</span>
                          {sortField === 'nb_rdv_appointments' && (
                            sortDirection === 'asc' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />
                          )}
                        </div>
                      </th>
                      <th
                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors"
                        onClick={() => handleSort('nb_rdv_prospecting')}
                      >
                        <div className="flex items-center space-x-1">
                          <span>RDV (Prospecting)</span>
                          {sortField === 'nb_rdv_prospecting' && (
                            sortDirection === 'asc' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />
                          )}
                        </div>
                      </th>
                      <th
                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors"
                        onClick={() => handleSort('nb_rdv')}
                      >
                        <div className="flex items-center space-x-1">
                          <span>Total RDV</span>
                          {sortField === 'nb_rdv' && (
                            sortDirection === 'asc' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />
                          )}
                        </div>
                      </th>
                      <th
                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors"
                        onClick={() => handleSort('jours_travailles')}
                      >
                        <div className="flex items-center space-x-1">
                          <span>Jours Travaillés</span>
                          {sortField === 'jours_travailles' && (
                            sortDirection === 'asc' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />
                          )}
                        </div>
                      </th>
                      <th
                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors"
                        onClick={() => handleSort('moy_calls_jour')}
                      >
                        <div className="flex items-center space-x-1">
                          <span>Moy. Calls/J</span>
                          {sortField === 'moy_calls_jour' && (
                            sortDirection === 'asc' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />
                          )}
                        </div>
                      </th>
                      <th
                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors"
                        onClick={() => handleSort('moy_rdv_jour')}
                      >
                        <div className="flex items-center space-x-1">
                          <span>Moy. RDV/J</span>
                          {sortField === 'moy_rdv_jour' && (
                            sortDirection === 'asc' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />
                          )}
                        </div>
                      </th>
                      <th
                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors"
                        onClick={() => handleSort('taux_conversion')}
                      >
                        <div className="flex items-center space-x-1">
                          <span>Taux Conv. (%)</span>
                          {sortField === 'taux_conversion' && (
                            sortDirection === 'asc' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />
                          )}
                        </div>
                      </th>
                      <th
                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors"
                        onClick={() => handleSort('taux_joignabilite')}
                      >
                        <div className="flex items-center space-x-1">
                          <span>Taux Joign. (%)</span>
                          {sortField === 'taux_joignabilite' && (
                            sortDirection === 'asc' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />
                          )}
                        </div>
                      </th>
                      <th
                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors"
                        onClick={() => handleSort('taux_connexion')}
                      >
                        <div className="flex items-center space-x-1">
                          <span>Taux Connexion (%)</span>
                          {sortField === 'taux_connexion' && (
                            sortDirection === 'asc' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />
                          )}
                        </div>
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {sortedSdrStats.map((stat, idx) => (
                      <tr
                        key={stat.sdr_id}
                        className={`cursor-pointer transition-colors ${idx % 2 === 0 ? 'bg-white hover:bg-blue-50' : 'bg-gray-50 hover:bg-blue-50'} ${selectedSdrId === stat.sdr_id ? 'bg-blue-100' : ''}`}
                        onClick={() => setSelectedSdrId(selectedSdrId === stat.sdr_id ? null : stat.sdr_id)}
                      >
                        <td className={`sticky left-0 z-10 px-6 py-4 whitespace-nowrap border-r border-gray-300 shadow-[2px_0_4px_rgba(0,0,0,0.1)] ${idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'} ${selectedSdrId === stat.sdr_id ? 'bg-blue-100' : ''}`}>
                          <div className="font-medium text-gray-900">{stat.sdr_name}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {stat.nb_call.toLocaleString()}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-gray-900">
                          {stat.nb_call_from_logs.toLocaleString()}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {stat.nb_echange.toLocaleString()}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-blue-600">
                          {stat.nb_connectes}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-orange-600">
                          {stat.nb_vocemails}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-blue-600">
                          {stat.nb_rdv_appointments}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-amber-600">
                          {stat.nb_rdv_prospecting}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-green-600">
                          {stat.nb_rdv}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {stat.jours_travailles}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {stat.moy_calls_jour}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {stat.moy_rdv_jour}
                        </td>
                        <td className={`px-6 py-4 whitespace-nowrap text-sm ${getPerformanceColor(stat.taux_conversion, 'taux_conversion')}`}>
                          {stat.taux_conversion}%
                        </td>
                        <td className={`px-6 py-4 whitespace-nowrap text-sm ${getPerformanceColor(stat.taux_joignabilite, 'taux_joignabilite')}`}>
                          {stat.taux_joignabilite}%
                        </td>
                        <td className={`px-6 py-4 whitespace-nowrap text-sm ${getPerformanceColor(stat.taux_connexion, 'taux_connexion')}`}>
                          {stat.taux_connexion}%
                        </td>
                      </tr>
                    ))}
                    {sortedSdrStats.length > 0 && (
                      <tr className="bg-blue-50 border-t-2 border-blue-200 font-bold">
                        <td className="sticky left-0 z-10 px-6 py-4 whitespace-nowrap text-gray-900 bg-blue-50 border-r border-gray-300 shadow-[2px_0_4px_rgba(0,0,0,0.1)]">
                          TOTAL
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {totalStats.nb_call.toLocaleString()}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 font-bold">
                          {totalStats.nb_call_from_logs.toLocaleString()}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {totalStats.nb_echange.toLocaleString()}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-blue-600 font-bold">
                          {totalStats.nb_connectes}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-orange-600 font-bold">
                          {totalStats.nb_vocemails}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-blue-600">
                          {totalStats.nb_rdv_appointments}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-amber-600">
                          {totalStats.nb_rdv_prospecting}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-green-600">
                          {totalStats.nb_rdv}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {totalStats.jours_travailles}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {totalStats.moy_calls_jour}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {totalStats.moy_rdv_jour}
                        </td>
                        <td className={`px-6 py-4 whitespace-nowrap text-sm ${getPerformanceColor(totalStats.taux_conversion, 'taux_conversion')}`}>
                          {totalStats.taux_conversion}%
                        </td>
                        <td className={`px-6 py-4 whitespace-nowrap text-sm ${getPerformanceColor(totalStats.taux_joignabilite, 'taux_joignabilite')}`}>
                          {totalStats.taux_joignabilite}%
                        </td>
                        <td className={`px-6 py-4 whitespace-nowrap text-sm ${getPerformanceColor(totalStats.taux_connexion, 'taux_connexion')}`}>
                          {totalStats.taux_connexion}%
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              )}

              {!sdrStatsLoading && sortedSdrStats.length === 0 && (
                <div className="text-center py-12">
                  <Users className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-500">Aucune donnée de prospection pour cette période</p>
                </div>
              )}
            </div>

            {selectedSdrId && (
              <div className="mt-6 grid grid-cols-1 lg:grid-cols-2 gap-6 p-6 border-t border-gray-200">
                <div className="bg-white rounded-lg border border-gray-200 p-6">
                  <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                    <Calendar className="h-5 w-5 text-blue-600" />
                    RDV Planifiés (Appointments)
                  </h3>
                  <div className="space-y-3 max-h-96 overflow-y-auto">
                    {selectedSdrDetails.appointments.length === 0 ? (
                      <p className="text-gray-500 text-sm">Aucun RDV planifié</p>
                    ) : (
                      selectedSdrDetails.appointments.map(apt => (
                        <div key={apt.id} className="border border-gray-200 rounded-lg p-3 hover:bg-gray-50 transition-colors">
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-sm font-medium text-gray-900">
                              {new Date(apt.date).toLocaleDateString('fr-FR')} à {apt.time}
                            </span>
                            <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                              apt.status === 'confirmed' ? 'bg-green-100 text-green-800' :
                              apt.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                              'bg-gray-100 text-gray-800'
                            }`}>
                              {apt.status}
                            </span>
                          </div>
                          <div className="text-sm text-gray-600">
                            <div>Prospect: {apt.prospect_name || 'Non renseigné'}</div>
                            {apt.client_name && <div>Client: {apt.client_name}</div>}
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </div>

                <div className="bg-white rounded-lg border border-gray-200 p-6">
                  <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                    <Phone className="h-5 w-5 text-amber-600" />
                    Journées de Prospection
                  </h3>
                  <div className="space-y-3 max-h-96 overflow-y-auto">
                    {selectedSdrDetails.prospectingDays.length === 0 ? (
                      <p className="text-gray-500 text-sm">Aucune journée de prospection</p>
                    ) : (
                      selectedSdrDetails.prospectingDays.map(day => (
                        <div key={day.id} className="border border-gray-200 rounded-lg p-3 hover:bg-gray-50 transition-colors">
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-sm font-medium text-gray-900">
                              {new Date(day.date).toLocaleDateString('fr-FR')}
                            </span>
                          </div>
                          {day.client_name && (
                            <div className="text-sm text-gray-600 mb-2">
                              Client: {day.client_name}
                            </div>
                          )}
                          <div className="grid grid-cols-3 gap-2 text-sm">
                            <div>
                              <span className="text-gray-500">Appels:</span>
                              <span className="ml-1 font-semibold text-gray-900">{day.calls_count}</span>
                            </div>
                            <div>
                              <span className="text-gray-500">Conv:</span>
                              <span className="ml-1 font-semibold text-gray-900">{day.conversations_count}</span>
                            </div>
                            <div>
                              <span className="text-gray-500">RDV:</span>
                              <span className="ml-1 font-semibold text-green-600">{day.rdvs_booked}</span>
                            </div>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              </div>
            )}

            {selectedSdrId && (
              <div className="mt-6 p-6 border-t border-gray-200">
                <div className="bg-white rounded-lg border border-gray-200 p-6">
                  <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                    <Phone className="h-5 w-5 text-green-600" />
                    Historique des Appels
                  </h3>
                  <div className="space-y-3 max-h-96 overflow-y-auto">
                    {selectedSdrDetails.callLogs.length === 0 ? (
                      <p className="text-gray-500 text-sm">Aucun appel enregistré</p>
                    ) : (
                      selectedSdrDetails.callLogs.map(log => (
                        <div key={log.id} className="border border-gray-200 rounded-lg p-3 hover:bg-gray-50 transition-colors">
                          <div className="flex items-center justify-between">
                            <span className="text-sm font-medium text-gray-900">
                              {new Date(log.created_at).toLocaleDateString('fr-FR')} à {new Date(log.created_at).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
                            </span>
                            <span className={`px-3 py-1 text-xs font-medium rounded-full ${
                              log.call_type === 'connected' ? 'bg-blue-100 text-blue-800' :
                              log.call_type === 'voicemail' ? 'bg-orange-100 text-orange-800' :
                              'bg-gray-100 text-gray-800'
                            }`}>
                              {log.call_type === 'connected' ? 'Connecté' :
                               log.call_type === 'voicemail' ? 'Vocemail' :
                               log.call_type}
                            </span>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default CampagnesOverviewPage;
